import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { hasKnownEntityName } from '@kirin/api-client/helpers/source-details';
import { cn } from '@/lib/utils';
import type { AddFromAlertScope } from '@/hooks/api/useAddPolicyExceptionFromAlert';
import { AlertAddToListButton } from '@/components/AlertAddToListButton';
import { AlertSeverityOverrideCell } from '@/components/AlertSeverityOverrideCell';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { ScanDetailFormattedText } from '@/components/ScanDetailFormattedText';
import { AlertDescription } from './AlertDescription';
import { AlertOsvComponentSection } from './AlertOsvComponentSection';
import { AlertSourceDetails } from './AlertSourceDetails';
import { AlertSecretDetails } from './AlertSecretDetails';
import { hasSecretDetails } from './AlertSecretDetails.helpers';
import { DetectionLogic } from './DetectionLogic';
import { WhyThisFired } from './WhyThisFired';
import { AccentCard } from './AccentCard';
import { AggregateScoreChip } from './AggregateScoreChip';
import { SecretConfidenceBadge } from './SecretConfidenceBadge';
import {
  AlertFactsStrip,
  type AlertFactsStripVariant,
} from './AlertFactsStrip';
import { AlertActionsBar } from './AlertActionsBar';
import { AlertEvidenceExcerpt } from './AlertEvidenceExcerpt';
import { RecommendedActions } from './RecommendedActions';
import { AlertFeedbackFields } from './AlertFeedbackFields';
import { AlertSessionId } from './session-timeline/AlertSessionId';
import { ToolCallCard } from './ToolCallCard';
import { ToolCallVerdictCard } from './ToolCallVerdictCard';
import {
  deriveToolCallVerdict,
  hasToolCallVerdictContent,
  toolCallToolName,
} from './tool-call-detail.helpers';
import { TOOL_CALL_FACT_TOOLTIPS } from './tool-call-copy';
import { stripMcpToolResponseLocationPrefix } from './alert-location';
import {
  hasDetectedOnClientCard,
  hasLocationCard,
  hasServerJsonCard,
} from './alert-detail-cards.helpers';
import {
  EVIDENCE_NOT_APPLICABLE_COPY,
  isEvidenceNotApplicable,
} from './evidence-state';
import { useAlertDetailModel } from './useAlertDetailModel';

interface AlertDetailCardsProps {
  alert: GroupedAlert;
  className?: string;
  /** Org scope for scan-result links; falls back to the route param when omitted. */
  organizationId?: string;
  /** Together with {@link alertKey}, surfaces the "Add to Allow/Block List" action. */
  scope?: AddFromAlertScope;
  /** Alert key, threaded into the add-to-list action. */
  alertKey?: string;
  /**
   * KIR-4281 — `'occurrence'` renders this panel for ONE occurrence. Suppresses
   * Mute / Exclude / Edit Policy in the actions bar (they are keyed on
   * `alertKey` and would act on the whole group), the severity override and
   * Status / Comment, which are the group's too. An absent `mute` is not
   * enough: it renders a live *Mute* button, not no button.
   */
  variant?: AlertFactsStripVariant;
  /** KIR-4281 — see {@link AlertFactsStrip}'s prop of the same name. */
  endpointLabel?: string | null;
  /** Shows "Show Instances" in the actions bar; omitted on the occurrences page. */
  onShowOccurrences?: () => void;
  /**
   * Shown without its alerts-table row (the alert window): the facts strip
   * adds the row's File, and First seen under Last seen; the title row carries
   * the session ID with a copy button.
   */
  standalone?: boolean;
}

/**
 * KIR-4481 — the redesigned Alert Details panel: a facts strip plus grouped,
 * colour-accented cards, in place of {@link AlertDetailCard}'s flat label list.
 *
 * Presentation only, sharing {@link useAlertDetailModel} with the legacy card so
 * the two layouts cannot drift. It re-groups the same data into: a facts strip,
 * "What this means", "Location", "Detected on client" (which now also hosts
 * Detection Logic), and "Server definition (JSON)". A card with no data for this
 * alert is hidden. Where the flat card carried extra title-bar chips (aggregate
 * score, secret confidence), those are preserved here under the same gates.
 *
 * KIR-4836 — `tool-call-scan` swaps Location + Detected on client for its own
 * "Tool call" + "Why Kirin AI flagged this" pair. Only that type is affected.
 */
export function AlertDetailCards({
  alert,
  className,
  organizationId: organizationIdProp,
  scope,
  alertKey,
  variant = 'group',
  endpointLabel,
  onShowOccurrences,
  standalone,
}: AlertDetailCardsProps): ReactElement {
  const {
    organizationId,
    sourceDetailsEnabled,
    capabilityDetectionEnabled,
    secretDetailsEnabled,
    detectionLogicEnabled,
    gitleaksRulesPath,
    osvComponent,
    osvAdvisoryId,
    configJsonBlock,
    secretDetails,
    label,
    description,
    cardSections,
    mitigation,
    surroundingText,
    isExtensionSecurityAlert,
    mcpLocation,
    locationForDisplay,
    scanResultLink,
    chatExcerpt,
    commandExcerpt,
    toolCallExcerpt,
    suppressRedundantLocation,
  } = useAlertDetailModel(alert, organizationIdProp);

  /*
    KIR-4836: `tool-call-scan` gets its own pair of cards — "Tool call" and "Why
    Kirin AI flagged this" — in place of Location + Detected on client. Location
    held nothing but the tool name (the agent writes it there), which now sits in
    the facts strip; the generic Detection Logic rows are re-presented as the
    score bar and the "Show detection details" panel. Scoped to this one type, so
    every other alert keeps today's layout exactly.
  */
  const isToolCallScan = alert.type === 'tool-call-scan';
  const toolCallEvidence = alert.toolCallScan;
  const toolName = toolCallToolName(alert);
  const toolCallVerdict = deriveToolCallVerdict(toolCallEvidence);
  const showToolCallCard =
    isToolCallScan &&
    (toolName !== undefined ||
      toolCallVerdict !== null ||
      Boolean(toolCallExcerpt));
  /*
    The Why-flagged card is the canonical home for this type's per-finding
    reason, but "What this means" prints the same string in two supported
    configs: as its `Detected` sub-block when Detection Logic is off (the model
    only strips that when the flag is ON — KIR-4272), and as the raw fallback
    paragraph when the alerts-revamp copy is off (no sections at all). Strip the
    sub-block here; and where there is no static copy left to show, leave the
    reason to that fallback rather than printing it under both cards.
  */
  const meansSections =
    isToolCallScan && cardSections != null
      ? {
          whatHappened: cardSections.whatHappened,
          whyItMatters: cardSections.whyItMatters,
        }
      : cardSections;
  // Mirrors AlertDescription's own fallback condition: with both static halves
  // empty it renders `fallbackDescription`, which is the raw finding text.
  const meansCardShowsReason =
    isToolCallScan &&
    (meansSections?.whatHappened.trim() ?? '') === '' &&
    (meansSections?.whyItMatters.trim() ?? '') === '';
  // The reason counts towards the card's content only when this card is the one
  // rendering it — otherwise a row with no scan payload opens an empty card.
  const showToolCallVerdictCard =
    isToolCallScan &&
    hasToolCallVerdictContent(
      toolCallEvidence,
      meansCardShowsReason ? undefined : alert.description
    );

  // KIR-4212: for a chat-security alert with Detection Logic on, the Location
  // STRING just re-joins the key_factors the "Detected on client" card already
  // shows — suppress that sub-block only. The card itself must still open for an
  // OSV component (silent data loss otherwise — Gidi @:91).
  const showLocationString =
    !suppressRedundantLocation && Boolean(mcpLocation || locationForDisplay);
  const showLocationCard =
    !isToolCallScan &&
    hasLocationCard({
      location: alert.location,
      sourceDetails: alert.sourceDetails,
      sourceDetailsEnabled,
    }) &&
    (Boolean(osvComponent) || showLocationString);
  // Legacy weighted-score "Why this fired" (KIR-3781) — superseded by Detection
  // Logic when its flag is on. Kept for the flag-off state so extension-security
  // alerts don't lose the breakdown (mirrors AlertDetailCard). Folded into the
  // "Detected on client" card, the same home as Detection Logic.
  const showWhyThisFired =
    !detectionLogicEnabled &&
    capabilityDetectionEnabled &&
    isExtensionSecurityAlert;
  // KIR-4269: a coverage-gap finding (a scan that could not complete) has no
  // detected content. The card renders an explicit "not applicable" evidence
  // state — distinct from "unavailable" — and Detection Logic is suppressed for
  // these types so the card does not present a reason as if something was found.
  const evidenceNotApplicable = isEvidenceNotApplicable(alert.type);
  const hasDetectedContent =
    hasDetectedOnClientCard({
      sourceDetails: alert.sourceDetails,
      sourceDetailsEnabled,
      secretDetails,
      secretDetailsEnabled,
      detectionLogicEnabled,
    }) ||
    showWhyThisFired ||
    // KIR-4212: the chat-security excerpt is client-detected evidence; it lives
    // in this card next to Detection Logic. Force the card open when the excerpt
    // exists so it shows even on the flag-off path (no Detection Logic to host it).
    Boolean(chatExcerpt) ||
    // KIR-4213: same for the command-file evidence excerpt.
    Boolean(commandExcerpt) ||
    Boolean(toolCallExcerpt) ||
    // KIR-4269: surface the honest not-applicable state even when nothing
    // else would open the card.
    evidenceNotApplicable;
  // KIR-4836: the tool-call pair replaces this card for that type. Applied to
  // the whole disjunction above — `toolCallExcerpt` alone would otherwise still
  // force the card open and duplicate the command.
  const showDetectedCard = !isToolCallScan && hasDetectedContent;
  const showServerJsonCard = hasServerJsonCard({
    sourceDetails: alert.sourceDetails,
    sourceDetailsEnabled,
  });

  const showSourceDetails =
    alert.sourceDetails?.kind === 'pii' ||
    (sourceDetailsEnabled &&
      alert.sourceDetails != null &&
      hasKnownEntityName(alert.sourceDetails));
  const showSecretDetails =
    secretDetailsEnabled && hasSecretDetails(secretDetails);

  return (
    <div
      data-testid="alert-detail-cards"
      className={cn(
        // Full-width background band that fills the whole (often very wide,
        // horizontally-scrolling) alerts-table row, so the expanded region never
        // reads as a box floating in empty space.
        //
        // `whitespace-normal` resets the `white-space: nowrap` inherited from the
        // DataTable cell this panel renders inside — without it, long card text
        // (Detection Logic excerpts, matched-text) never wraps and overflows the
        // card. Elements that need `nowrap` (facts-strip `truncate`, the mono
        // `code` blocks with `whitespace-pre`) set their own and are unaffected.
        'w-full min-w-0 whitespace-normal rounded-lg border bg-white p-3 dark:bg-slate-800',
        className
      )}
    >
      {/* Inner content is capped and pinned left so the two-column
          Location/Detected row stays readable instead of stretching across an
          ultra-wide, horizontally-scrolling table row, and stays visible as that
          row scrolls. 1280px ≈ the widest the two columns read comfortably at
          (a large-desktop content width); beyond it the cards just gain
          whitespace, not information. */}
      <div className="sticky left-0 flex min-w-0 max-w-[1280px] flex-col gap-3">
        {/* Title bar, then the actions bar. Add to Allow/Block List rides at
            the bar's far end, so the title row's height and the other actions'
            positions never depend on whether it renders. Severity and secret
            confidence sit on the facts strip's Risk score line. */}
        <div className="flex flex-col gap-3 border-b pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{label}</span>
            {/* KIR-4481: preserve the flat card's catalog aggregate score
                (distinct from the facts-strip Risk score, which is
                severity-derived) under the same gate. */}
            {capabilityDetectionEnabled && isExtensionSecurityAlert && (
              <AggregateScoreChip score={alert.scoreBreakdown?.score} />
            )}
            {standalone && alert.sessionLink && (
              <AlertSessionId
                sessionId={alert.sessionLink.sessionId}
                className="ml-auto"
              />
            )}
          </div>
          <AlertActionsBar
            alert={alert}
            organizationId={organizationId}
            variant={variant}
            {...(onShowOccurrences ? { onShowOccurrences } : {})}
            endSlot={
              scope && alertKey ? (
                <AlertAddToListButton
                  scope={scope}
                  sourceDetails={alert.sourceDetails}
                  alertKey={alertKey}
                  analyticsContext={{
                    severity: alert.severity,
                    type: alert.type,
                  }}
                  className="ml-auto shrink-0"
                />
              ) : null
            }
          />
        </div>

        {/* Facts strip */}
        <AlertFactsStrip
          alert={alert}
          {...(standalone ? { standalone } : {})}
          riskBadges={
            <>
              {/* The alerts table's own severity cell, so the look (and, for
                  managers, the override menu) match. One occurrence has no
                  group severity to override: plain badge, same style. */}
              {variant === 'occurrence' ? (
                <SeverityBadge riskLevel={alert.severity} showIcon />
              ) : (
                <AlertSeverityOverrideCell
                  scope={
                    organizationId
                      ? { kind: 'organization', organizationId }
                      : { kind: 'account' }
                  }
                  alert={alert}
                />
              )}
              {secretDetailsEnabled && typeof alert.entropy === 'number' && (
                // Compacted directly: its tooltip trigger replaces the badge's
                // data-slot, which the strip's compacting rule keys on.
                <SecretConfidenceBadge
                  entropy={alert.entropy}
                  className="py-0.5"
                />
              )}
            </>
          }
          {...(endpointLabel !== undefined ? { endpointLabel } : {})}
          {...(isToolCallScan
            ? {
                tooltips: TOOL_CALL_FACT_TOOLTIPS,
                ...(toolName !== undefined ? { toolName } : {}),
              }
            : {})}
        />

        {/* Card: What this means */}
        <AccentCard category="what-this-means" title="What this means">
          <AlertDescription
            sections={meansSections}
            fallbackDescription={description}
            {...(surroundingText ? { surroundingText } : {})}
            className="text-sm text-muted-foreground"
          />
        </AccentCard>

        {/* KIR-4836: Cards: Tool call + Why Kirin AI flagged this (tool-call-scan
            only). Full width rather than in the two-column grid below — the
            command and the score bar both read badly at half width. */}
        {showToolCallCard && (
          <ToolCallCard
            verdict={toolCallVerdict}
            {...(toolName !== undefined ? { toolName } : {})}
            {...(toolCallExcerpt !== undefined
              ? { excerpt: toolCallExcerpt }
              : {})}
          />
        )}
        {showToolCallVerdictCard && (
          <ToolCallVerdictCard
            {...(toolCallEvidence !== undefined
              ? { evidence: toolCallEvidence }
              : {})}
            {...(alert.description !== undefined && !meansCardShowsReason
              ? { reason: alert.description }
              : {})}
          />
        )}

        {/* Cards: Location + Detected on client (side by side on wide viewports) */}
        {(showLocationCard || showDetectedCard) && (
          // `min-w-0` so this grid (a flex item of the column above) can shrink
          // to the capped width; without it, long card content (e.g. a wide
          // Detection Logic excerpt) forces the grid — and the "Detected on
          // client" column — to overflow the panel instead of wrapping inside.
          <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
            {showLocationCard && (
              <AccentCard category="location" title="Location">
                {/* The location/file line and the OSV component are siblings, not
                    either/or (KIR-4190): a vulnerable-dependency alert with a
                    config-file location must show both, matching AlertDetailCard. */}
                {showLocationString && (
                  <div className="min-w-0 max-w-full rounded bg-slate-100 px-2 py-1 font-mono text-xs dark:bg-slate-700">
                    {mcpLocation ? (
                      <>
                        {mcpLocation.identity && (
                          <div>{mcpLocation.identity}</div>
                        )}
                        <div className="mt-2 text-muted-foreground">
                          Tool description:
                        </div>
                        <div className="break-words whitespace-pre-wrap">
                          {mcpLocation.before}
                          <mark className="rounded bg-amber-100 px-1 py-0.5 font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                            {mcpLocation.match}
                          </mark>
                          {mcpLocation.after}
                        </div>
                      </>
                    ) : (
                      // KIR-4213: break-all so a long file path (no spaces to
                      // wrap on) shows in full; scoped to this branch so it never
                      // breaks the MCP identity line above mid-word.
                      <div className="break-all">
                        <ScanDetailFormattedText
                          location={
                            stripMcpToolResponseLocationPrefix(
                              locationForDisplay
                            ) ?? ''
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
                {osvComponent && (
                  <AlertOsvComponentSection
                    osvComponent={osvComponent}
                    hideHeading
                    {...(osvAdvisoryId ? { osvId: osvAdvisoryId } : {})}
                    {...(mcpLocation || locationForDisplay
                      ? { className: 'mt-2' }
                      : {})}
                  />
                )}
              </AccentCard>
            )}

            {showDetectedCard && (
              <AccentCard
                category="detected-on-client"
                title="Detected on client"
              >
                {/* KIR-4269: coverage-gap findings have no detected content —
                    state that honestly, in a form that reads differently from an
                    "unavailable" reason and never implies nothing happened. */}
                {evidenceNotApplicable && (
                  <p
                    className="text-sm text-muted-foreground"
                    data-testid="evidence-not-applicable"
                  >
                    {EVIDENCE_NOT_APPLICABLE_COPY}
                  </p>
                )}
                {detectionLogicEnabled && !evidenceNotApplicable && (
                  <DetectionLogic
                    alert={alert}
                    sourceDetailsAvailable={sourceDetailsEnabled}
                    secretDetailsAvailable={secretDetailsEnabled}
                    {...(gitleaksRulesPath !== undefined
                      ? { gitleaksRulesPath }
                      : {})}
                    className="mt-0"
                  />
                )}
                {/* KIR-4212: the flagged chat content (code / commands / tool
                    calls), masked at ingest — the evidence behind Detection
                    Logic's paraphrased factors. Same masked-`<pre>` styling as
                    the legacy card and the PII/secret panels. */}
                {chatExcerpt && (
                  <div className="mt-2 flex flex-col gap-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Chat excerpt
                    </p>
                    <pre className="max-h-72 overflow-auto rounded bg-slate-100 p-2 font-mono text-xs break-words whitespace-pre-wrap dark:bg-slate-700">
                      {chatExcerpt}
                    </pre>
                  </div>
                )}
                {/* KIR-4213: the full flagged command (classifier key_factors),
                    masked at ingest — the whole stored excerpt is shown (backend
                    already bounds + middle-truncates it), so the tail is never
                    hidden the way `location` cut it. */}
                {commandExcerpt && (
                  <AlertEvidenceExcerpt
                    label="Command excerpt"
                    content={commandExcerpt}
                  />
                )}
                {toolCallExcerpt && (
                  <AlertEvidenceExcerpt
                    label="Tool-call excerpt"
                    content={toolCallExcerpt}
                  />
                )}
                {showWhyThisFired && (
                  <WhyThisFired
                    type={alert.type}
                    scoreBreakdown={alert.scoreBreakdown}
                  />
                )}
                {showSourceDetails && alert.sourceDetails && (
                  <AlertSourceDetails
                    sourceDetails={alert.sourceDetails}
                    hideHeading
                    hideConfigJson
                  />
                )}
                {showSecretDetails && (
                  <AlertSecretDetails
                    details={secretDetails}
                    organizationId={organizationId}
                    variant={variant}
                  />
                )}
                {scanResultLink && (
                  <Link
                    to={scanResultLink}
                    className="mt-3 inline-flex text-sm font-medium text-primary underline-offset-4 hover:underline"
                  >
                    View full scan result →
                  </Link>
                )}
              </AccentCard>
            )}
          </div>
        )}

        {/* Card: Server definition (JSON). Collapsed by default: the config
            stanza can be long and, though the backend pre-masks secrets
            (tokens / Authorization / Bearer / credentials), keeping it behind a
            toggle matches the legacy "More details" panel rather than dumping the
            raw server config the moment the row expands (KIR-4481 review). */}
        {showServerJsonCard && configJsonBlock && (
          <AccentCard category="server-json" title="Server definition · JSON">
            <details className="group">
              <summary className="inline-flex cursor-pointer select-none items-center gap-1 text-xs font-medium text-muted-foreground [&::-webkit-details-marker]:hidden">
                <ChevronRight
                  className="h-4 w-4 group-open:hidden"
                  aria-hidden
                />
                <ChevronDown
                  className="hidden h-4 w-4 group-open:block"
                  aria-hidden
                />
                <span className="group-open:hidden">Show JSON</span>
                <span className="hidden group-open:inline">Hide JSON</span>
              </summary>
              <pre className="mt-2 max-h-72 overflow-auto rounded bg-slate-100 p-2 font-mono text-xs dark:bg-slate-700">
                {configJsonBlock}
              </pre>
            </details>
          </AccentCard>
        )}

        {/* Card: Recommended actions (KIR-4271). Reads as guidance, separate
            from Mute / Exclude / Add to list in the actions bar.
            `mitigation` is already gated on `alert_recommended_actions` +
            `alerts_revamp` and is null when the mapping has no approved copy,
            so the section is simply absent then. */}
        {mitigation != null && (
          <AccentCard
            category="recommended-actions"
            title="Recommended actions"
          >
            <RecommendedActions mitigation={mitigation} />
          </AccentCard>
        )}

        {/* KIR-4440 Status + Comment, set while reading the alert. The group's
            feedback, so never on one occurrence. */}
        {variant !== 'occurrence' && organizationId && (
          <AlertFeedbackFields alert={alert} organizationId={organizationId} />
        )}
      </div>
    </div>
  );
}

AlertDetailCards.displayName = 'AlertDetailCards';
